import { useEffect, useState } from 'react';
import { configure, getSession, type AuthSession } from '@weretradeit/auth-client';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

const LOGIN_PORTAL = 'https://login.weretrade.com';

/**
 * Point the shared auth client at `login.weretrade.com` instead of its default.
 *
 * `@weretradeit/auth-client` defaults `apiBaseUrl` to `https://auth.weretrade.com/api/auth`, and
 * that hostname sits behind a Cloudflare managed challenge — it answers `403` with
 * `cf-mitigated: challenge` and no CORS headers at all. A `fetch()` cannot solve a challenge, so
 * `getSession()` failed for **every** visitor, reported `isAuthenticated: false`, and the effect
 * below bounced to the portal. The portal then saw a perfectly valid SSO session and sent the
 * browser straight back — an infinite login loop with no bad credential anywhere in it.
 *
 * `login.weretrade.com` is the same core-lion worker without the challenge, and authLion already
 * allowlists both live hostnames on it (verified: preflight `204`, GET `401` with
 * `access-control-allow-origin` echoed and `access-control-allow-credentials: true` for both
 * `hub.` and `meerkat.weretrade.com`).
 *
 * Configured at module scope so it is set before any hook runs. Note the sibling apps sidestep
 * this entirely — accountingFox proxies `/api/auth/me` through its own worker over the AUTH
 * service binding, which is same-origin and cannot be challenged or blocked by CORS. That is the
 * more robust shape and worth adopting here; this keeps the shared client but on a reachable host.
 */
configure({ apiBaseUrl: `${LOGIN_PORTAL}/api/auth` });

/**
 * authLion session gate.
 *
 * The shared auth client validates the SSO cookie against core-lion (see the `configure` call
 * above for which hostname and why), and unauthenticated users are bounced to the shared login
 * portal with a redirect back here. Never implement app-local auth.
 *
 * Both bounces below are one-shot by construction — the redirect leaves the page, so nothing
 * re-runs. The loop this app hit came from the *answer* being wrong, not from bouncing twice.
 */
export function useSession() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [status, setStatus] = useState<SessionStatus>('loading');

  useEffect(() => {
    let cancelled = false;

    // Local development runs against a local worker with no SSO cookie. Without this branch
    // every dev reload bounces to the production login portal, which makes the app impossible
    // to work on locally.
    //
    // This is safe because `import.meta.env.DEV` is substituted with the literal `false` at
    // build time, so the entire branch is dead-code-eliminated from any production bundle —
    // the shipped JS physically cannot contain it. Verified by grepping dist/ for the marker.
    if (import.meta.env.DEV) {
      setSession({
        isAuthenticated: true,
        user: { id: 0, username: 'local-dev-operator' },
      });
      setStatus('authenticated');
      return;
    }

    getSession()
      .then((result) => {
        if (cancelled) return;

        if (result.isAuthenticated) {
          setSession(result);
          setStatus('authenticated');
          return;
        }

        setStatus('anonymous');
        const redirect = encodeURIComponent(window.location.href);
        window.location.replace(`${LOGIN_PORTAL}/?redirect=${redirect}`);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail closed: an unreachable auth service must not render the app.
        setStatus('anonymous');
        const redirect = encodeURIComponent(window.location.href);
        window.location.replace(`${LOGIN_PORTAL}/?redirect=${redirect}`);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { session, status };
}
